import { formatINR } from "@/lib/brand";

/**
 * Reusable year-by-year breakdown: horizontal bars (extension of the
 * milestone pattern used on FutureCostCalculator) + a scrollable table.
 *
 * rows: [{ year: number, primary: number, segments?: [{ value, className, label }], columns: [{ label, value, accent }] }]
 * primaryLabel: eyebrow shown above the bars
 * tableHeaders: [string, ...]
 * testIdPrefix: e.g. "sip", "lumpsum" — used for data-testid hooks
 */
export default function YearBreakdown({
  rows,
  primaryLabel = "Year-by-year value",
  tableHeaders,
  testIdPrefix,
}) {
  if (!rows || rows.length === 0) return null;
  const maxPrimary = Math.max(...rows.map((r) => r.primary), 1);

  return (
    <div className="mt-8 space-y-8">
      {/* Chart (milestone-style horizontal bars) */}
      <div data-testid={`${testIdPrefix}-breakdown-chart`}>
        <div className="eyebrow mb-3">{primaryLabel}</div>
        <div className="space-y-2.5 max-h-[340px] overflow-y-auto pr-2">
          {rows.map((r) => {
            const totalSeg = (r.segments || []).reduce((s, x) => s + x.value, 0) || r.primary;
            return (
              <div key={`chart-${r.year}`}>
                <div className="flex justify-between text-[12px] mb-1">
                  <span className="num text-ink-soft">Year {r.year}</span>
                  <span className="num text-ink">{formatINR(r.primary)}</span>
                </div>
                <div className="w-full h-2 rounded-sm bg-paper border hairline overflow-hidden flex">
                  {r.segments && r.segments.length > 0 ? (
                    r.segments.map((s, i) => (
                      <div
                        key={i}
                        style={{ width: `${(s.value / maxPrimary) * 100}%` }}
                        className={`h-full transition-all duration-500 ${s.className}`}
                        title={s.label ? `${s.label}: ${formatINR(s.value)}` : undefined}
                      />
                    ))
                  ) : (
                    <div
                      style={{ width: `${(r.primary / maxPrimary) * 100}%` }}
                      className="h-full bg-gradient-to-r from-brass-deep to-brass transition-all duration-500"
                    />
                  )}
                  {r.segments && totalSeg < maxPrimary && (
                    <div style={{ width: `${((maxPrimary - totalSeg) / maxPrimary) * 100}%` }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {rows[0]?.segments && (
          <div className="flex flex-wrap gap-4 mt-3 text-[12px] text-ink-soft">
            {rows[0].segments.map((s, i) => (
              <span key={i} className="inline-flex items-center">
                <span className={`inline-block w-2 h-2 mr-1.5 ${s.legendClassName || s.className}`} />
                {s.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div>
        <div className="eyebrow mb-3">Year-by-year table</div>
        <div className="overflow-x-auto border hairline rounded-sm" data-testid={`${testIdPrefix}-breakdown-table`}>
          <table className="w-full text-[13.5px] min-w-[520px]">
            <thead>
              <tr className="border-b hairline bg-paper">
                {tableHeaders.map((h, i) => (
                  <th
                    key={i}
                    className="eyebrow text-left py-2.5 px-4 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={`row-${r.year}`}
                  className={idx % 2 === 0 ? "bg-transparent" : "bg-paper/40"}
                >
                  <td className="num text-ink py-2.5 px-4 whitespace-nowrap">
                    {r.year}
                  </td>
                  {r.columns.map((c, ci) => (
                    <td
                      key={ci}
                      className={`num py-2.5 px-4 whitespace-nowrap ${
                        c.accent ? "text-brass-deep" : "text-ink"
                      }`}
                    >
                      {c.value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
